/**
 * Le plus grand côté, après réduction.
 *
 * Le coût d'une lecture par un modèle de vision est proportionnel à la **surface** de
 * l'image : diviser le côté par quatre divise la facture par seize. Une photo de téléphone
 * fait couramment 4 000 px de large ; à 1 024 px, un ticket de caisse reste parfaitement
 * lisible — c'est la taille à laquelle on lit encore les centimes à l'œil sur un écran.
 *
 * Descendre plus bas ferait basculer les tickets thermiques pâles dans l'illisible, et une
 * lecture ratée coûte deux fois : les jetons dépensés, et la saisie manuelle quand même.
 */
const COTE_MAX = 1024;

/**
 * Qualité JPEG. 0,8 est le point où l'artefact de compression cesse de se voir sur du
 * texte imprimé ; au-delà, on paie des octets pour du grain de photo.
 */
const QUALITE = 0.8;

/** Les seuls types que le modèle sait lire — un PDF déposé reste valable, il n'est pas lu. */
const TYPES_LISIBLES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

export const estLisibleParIA = (fichier: File): boolean => TYPES_LISIBLES.includes(fichier.type);

/**
 * Réduit une photo de justificatif avant de l'envoyer à la lecture.
 *
 * **Dans le navigateur, et pas côté serveur** : c'est le seul endroit où la réduction
 * économise tout à la fois — les jetons du modèle, la bande passante du parent, et le
 * temps d'attente devant un formulaire qu'il regarde. L'original, lui, part **inchangé**
 * vers le stockage du dossier : c'est la pièce qui tranchera si la dépense est contestée,
 * et une pièce recompressée n'est plus tout à fait la pièce.
 *
 * Rend `null` si la réduction échoue — format que le navigateur ne décode pas, canvas
 * indisponible. L'appelant s'en passe : le formulaire se remplit à la main depuis toujours.
 */
export async function reduirePourLecture(fichier: File): Promise<Blob | null> {
  if (!estLisibleParIA(fichier)) return null;

  try {
    const image = await chargerImage(fichier);
    const echelle = Math.min(1, COTE_MAX / Math.max(image.width, image.height));

    // Une image déjà petite n'est pas agrandie : redessiner à l'identique ne ferait que
    // lui ajouter une génération de compression.
    const largeur = Math.round(image.width * echelle);
    const hauteur = Math.round(image.height * echelle);

    const canvas = document.createElement('canvas');
    canvas.width = largeur;
    canvas.height = hauteur;
    const contexte = canvas.getContext('2d');
    if (!contexte) return null;

    // Un fond blanc avant le dessin : un PNG à fond transparent deviendrait noir en JPEG,
    // et un ticket noir sur noir ne se lit pas.
    contexte.fillStyle = '#FFFFFF';
    contexte.fillRect(0, 0, largeur, hauteur);
    contexte.drawImage(image, 0, 0, largeur, hauteur);

    return await new Promise<Blob | null>((resoudre) => {
      canvas.toBlob((blob) => resoudre(blob), 'image/jpeg', QUALITE);
    });
  } catch {
    return null;
  }
}

/** Décode le fichier en image, en libérant l'URL objet quoi qu'il arrive. */
function chargerImage(fichier: File): Promise<HTMLImageElement> {
  return new Promise((resoudre, rejeter) => {
    const url = URL.createObjectURL(fichier);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resoudre(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      rejeter(new Error('Image indécodable.'));
    };
    image.src = url;
  });
}
